// A-11 — Modale de création de dossier (wizard 4 étapes).
// Étape 1 : type d'affaire + intitulé
// Étape 2 : client (sélection dans le répertoire ou création rapide)
// Étape 3 : intervenants (collaborateurs ajoutés au dossier)
// Étape 4 : juridiction + RG + AJ + dates clés (optionnel)
import { JurisdictionSelect } from './JurisdictionSelect';
import type { Jurisdiction } from '../../services/dossiers/jurisdictionService';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase, ChevronLeft, ChevronRight, Search, UserPlus, X,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { supabase } from '../../lib/supabaseClient';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  type CreateDossierInput,
  type DossierType,
  type DossierMemberRole,
  DOSSIER_TYPE_LABELS,
  setLeadCounsel,
} from '../../services/dossiers/dossierService';
import { useCreateDossier, dossiersKeys } from '../../hooks/queries/useDossiers';
import { useContacts, useCreateContact, useLinkContactToDossier } from '../../hooks/queries/useContacts';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { useAuthStore } from '../../store/useAuthStore';
import { Avatar } from '../ui/Avatar';
import {
  CollaboratorOptionRow,
  CollaboratorSelect,
  type CollaboratorOption,
} from '../collaborators/CollaboratorSelect';
import { getContactDisplayName, CONTACT_CATEGORY_LABELS, type Contact } from '../../services/contacts/contactService';
import type { TablesInsert } from '../../types/database.types';
import { applyFolderTemplate } from '../../services/files/filesService';
import { shouldApplyFolderTemplate } from '../../lib/folderTemplates';
import { creationDefaults, friseChoiceLabel, frisesOffered, offeredFrises, type ParcoursFamily } from '../../lib/dossiers/parcoursDefaults';
import { loadParcoursCatalog } from '../../services/dossiers/friseService';
import { FolderTemplatePicker } from '../files/FolderTemplatePicker';
import { DatePicker } from '../ui/DatePicker';
import { applyFicheDueDay, DEFAULT_FICHE_DUE_TIME } from '../../lib/tasks/taskDueTime';

interface DossierCreateModalProps {
  onClose: () => void;
  onCreated?: (dossierId: string) => void;
}

const TYPES: DossierType[] = [
  'civil', 'penal', 'commercial', 'social',
  'immobilier', 'famille', 'admin', 'autre',
];

type Step = 1 | 2 | 3 | 4;

const STEP_TITLES: Record<Step, string> = {
  1: 'Parcours et intitulé',
  2: 'Client du dossier',
  3: 'Responsable du traitement',
  4: 'Juridiction et audience',
};

const ROLE_LABELS: Record<Exclude<DossierMemberRole, 'lead_counsel'>, string> = {
  counsel: 'Avocat intervenant',
  viewer:  'Lecteur',
};

interface NewClient {
  kind: 'individual' | 'corporate';
  first_name: string;
  last_name: string;
  company_name: string;
  email: string;
  phone: string;
}

interface MemberDraft {
  collaboratorId: string;
  role: Exclude<DossierMemberRole, 'lead_counsel'>;
}

export function DossierCreateModal({ onClose, onCreated }: DossierCreateModalProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const currentCollab = useAuthStore((s) => s.collaborator);

  const createDossier = useCreateDossier();
  const createContact = useCreateContact();
  const linkContact = useLinkContactToDossier();

  const [step, setStep] = useState<Step>(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Étape 1
  const [type, setType] = useState<DossierType>('autre');
  const [title, setTitle] = useState('');
  const [familySlug, setFamilySlug] = useState('contentieux');
  const [friseSlug, setFriseSlug] = useState('contentieux');
  const [folderTemplate, setFolderTemplate] = useState<string>('none');
  const parcours = useQuery({ queryKey: ['dossier-parcours-catalog'], queryFn: loadParcoursCatalog });
  const seededParcours = useRef(false);

  useEffect(() => {
    if (seededParcours.current) return;
    const defaults = creationDefaults(parcours.data ?? []);
    if (!defaults) return;
    seededParcours.current = true;
    setFamilySlug(defaults.familySlug);
    setFriseSlug(defaults.friseSlug);
    setFolderTemplate(defaults.folderSlug ?? 'none');
  }, [parcours.data]);

  // Étape 2 — client
  const [clientMode, setClientMode] = useState<'existing' | 'new' | 'skip'>('existing');
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<Contact | null>(null);
  const [newClient, setNewClient] = useState<NewClient>({
    kind: 'individual',
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone: '',
  });
  const { data: contactResults } = useContacts({
    search: clientSearch.trim() || undefined,
    category: ['client'],
  });

  // Étape 3 — responsable + intervenants
  const { data: collaborators } = useCollaborators();
  const [leadId, setLeadId] = useState('');
  const [members, setMembers] = useState<MemberDraft[]>([]);
  useEffect(() => {
    if (currentCollab?.id) setLeadId((current) => current || currentCollab.id);
  }, [currentCollab?.id]);
  const leadOptions = useMemo<CollaboratorOption[]>(
    () => (collaborators ?? [])
      .filter((c) => c.is_active)
      .map((c) => ({
        id: c.id,
        full_name: c.full_name,
        email: c.email,
        role: c.role,
        avatar_url: c.avatar_url,
      })),
    [collaborators],
  );
  const availableCollaborators = useMemo(
    () => leadOptions.filter(
      (c) => c.id !== currentCollab?.id
        && c.id !== leadId
        && !members.some((m) => m.collaboratorId === c.id),
    ),
    [leadOptions, currentCollab?.id, leadId, members],
  );

  function chooseLead(id: string) {
    setLeadId(id);
    setMembers((current) => current.filter((m) => m.collaboratorId !== id));
  }

  // Étape 4 — optionnels
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction | null>(null);
  const [chambre, setChambre] = useState('');
  const [rgNumber, setRgNumber] = useState('');
  const [hearingDay, setHearingDay] = useState('');
  const [hearingTime, setHearingTime] = useState(DEFAULT_FICHE_DUE_TIME);

  const canNext1 = title.trim().length > 0;
  const canNext2 = clientMode === 'skip'
    || (clientMode === 'existing' && !!selectedClient)
    || (clientMode === 'new' && hasNewClientName(newClient));

  function next() {
    if (step < 4) setStep((s) => (s + 1) as Step);
  }
  function back() {
    if (step > 1) setStep((s) => (s - 1) as Step);
  }

  async function submit() {
    setSubmitting(true);
    setSubmitError(null);

    try {
      // 1. Créer le dossier
      const payload: CreateDossierInput = {
        title: title.trim(),
        type_affaire: type,
        frise_slug: friseSlug,
        juridiction: jurisdiction?.name ?? null,
        chambre: jurisdiction ? chambre.trim() || null : null,
        rg_number: rgNumber.trim() || null,
        next_hearing_at: hearingDay ? applyFicheDueDay(hearingDay, hearingTime).due_datetime : null,
      };
      const dossier = await createDossier.mutateAsync(payload);

      // 2. Lier le client (sauf si "skip")
      const partialErrors: string[] = [];
      let contactId: string | null = null;

      if (clientMode === 'existing' && selectedClient) {
        contactId = selectedClient.id;
      } else if (clientMode === 'new' && hasNewClientName(newClient)) {
        try {
          const created = await createContact.mutateAsync({
            kind: newClient.kind,
            first_name: newClient.kind === 'individual' ? newClient.first_name.trim() || null : null,
            last_name:  newClient.kind === 'individual' ? newClient.last_name.trim()  || null : null,
            company_name: newClient.kind === 'corporate' ? newClient.company_name.trim() || null : null,
            email: newClient.email.trim() || null,
            phone: newClient.phone.trim() || null,
            contact_category: 'client',
          });
          contactId = created.id;
        } catch (e) {
          partialErrors.push(`Création du contact : ${e instanceof Error ? e.message : 'erreur'}`);
        }
      }

      if (contactId) {
        try {
          await linkContact.mutateAsync({
            dossierId: dossier.id,
            contactId,
            role: 'client',
            isPrimary: true,
          });
        } catch (e) {
          partialErrors.push(`Liaison du client : ${e instanceof Error ? e.message : 'erreur'}`);
        }
      }

      // 3. Ajouter les intervenants (le responsable choisi est traité ensuite)
      const extraMembers = members.filter((m) => m.collaboratorId !== leadId);
      if (extraMembers.length > 0) {
        const rows: TablesInsert<'dossier_members'>[] = extraMembers.map((m) => ({
          dossier_id: dossier.id,
          collaborator_id: m.collaboratorId,
          role: m.role,
          can_view_documents:      true,
          can_edit_documents:      m.role !== 'viewer',
          can_view_internal_notes: true,
          can_edit_internal_notes: m.role !== 'viewer',
          can_view_billing:        false,
          can_edit_billing:        false,
          can_view_time_tracking:  true,
        }));
        const { error } = await supabase.from('dossier_members').insert(rows);
        if (error) {
          partialErrors.push(`Ajout des intervenants : ${error.message}`);
        } else {
          qc.invalidateQueries({ queryKey: dossiersKeys.members(dossier.id) });
        }
      }

      // Le créateur est responsable à l'insertion. S'il en a nommé un autre,
      // on l'ajoute puis on lui transfère le rôle.
      const creatorId = currentCollab?.id;
      if (leadId && creatorId && leadId !== creatorId) {
        const { error: leadInsertError } = await supabase.from('dossier_members').insert({
          dossier_id: dossier.id,
          collaborator_id: leadId,
          role: 'counsel',
          can_view_documents: true,
          can_edit_documents: true,
          can_view_internal_notes: true,
          can_edit_internal_notes: true,
          can_view_billing: false,
          can_edit_billing: false,
          can_view_time_tracking: true,
        });
        if (leadInsertError) {
          throw new Error(`Ajout du responsable : ${leadInsertError.message}`);
        }
        await setLeadCounsel(dossier.id, leadId);
        qc.invalidateQueries({ queryKey: dossiersKeys.members(dossier.id) });
      }

      if (shouldApplyFolderTemplate(folderTemplate, 0) && folderTemplate !== 'none') {
        try {
          const result = await applyFolderTemplate(dossier.id, folderTemplate);
          if (result.skipped) {
            partialErrors.push('Arborescence : déjà des sous-dossiers, non appliquée.');
          }
        } catch (e) {
          partialErrors.push(`Arborescence : ${e instanceof Error ? e.message : 'erreur'}`);
        }
      }

      // 4. Succès — naviguer même en cas d'erreurs partielles
      onCreated?.(dossier.id);
      navigate(`/dossiers/${dossier.id}`);
      onClose();

      if (partialErrors.length > 0) {
        // Le dossier existe mais certaines associations ont échoué.
        // On laisse l'utilisateur ajuster depuis les onglets dédiés.
        // eslint-disable-next-line no-console
        console.warn('[DossierCreate] erreurs partielles :', partialErrors);
      }
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Erreur à la création du dossier.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-cime shadow-xl w-full max-w-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div className="flex items-center gap-2.5">
            <Briefcase className="w-4 h-4 text-cime-primary" />
            <div>
              <h2 className="font-heading text-lg font-semibold text-cime-text-primary">
                Nouveau dossier
              </h2>
              <p className="text-xs text-cime-text-muted">
                {step}/4 · {STEP_TITLES[step]}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-1 px-5 pt-3">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={cn(
                'h-1 flex-1 rounded-full transition-colors',
                s <= step ? 'bg-cime-primary' : 'bg-neutral-200',
              )}
            />
          ))}
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4 min-h-[320px] max-h-[60vh] overflow-y-auto">
          {step === 1 && (
            <Step1
              families={parcours.data ?? []}
              familySlug={familySlug}
              setFamilySlug={(slug) => {
                setFamilySlug(slug);
                const family = (parcours.data ?? []).find((item) => item.slug === slug);
                const frise = frisesOffered(family).find((item) => item.isDefault) ?? frisesOffered(family)[0];
                setFriseSlug(frise?.slug ?? '');
                const folder = family?.folders.find((item) => item.offered && item.isDefault);
                setFolderTemplate(folder?.slug ?? 'none');
              }}
              friseSlug={friseSlug}
              setFriseSlug={(slug) => {
                setFriseSlug(slug);
                const owner = (parcours.data ?? []).find((family) =>
                  family.frises.some((frise) => frise.slug === slug),
                );
                if (owner) setFamilySlug(owner.slug);
              }}
              type={type} setType={setType}
              title={title} setTitle={setTitle}
              folderTemplate={folderTemplate} setFolderTemplate={setFolderTemplate}
            />
          )}

          {step === 2 && (
            <Step2
              mode={clientMode} setMode={setClientMode}
              search={clientSearch} setSearch={setClientSearch}
              results={contactResults ?? []}
              selected={selectedClient} setSelected={setSelectedClient}
              newClient={newClient} setNewClient={setNewClient}
            />
          )}

          {step === 3 && (
            <Step3
              members={members} setMembers={setMembers}
              available={availableCollaborators}
              leadOptions={leadOptions}
              leadId={leadId}
              onLeadChange={chooseLead}
            />
          )}

          {step === 4 && (
            <Step4
              jurisdiction={jurisdiction} setJurisdiction={setJurisdiction}
              chambre={chambre} setChambre={setChambre}
              rgNumber={rgNumber} setRgNumber={setRgNumber}
              hearingDay={hearingDay} setHearingDay={setHearingDay}
              hearingTime={hearingTime} setHearingTime={setHearingTime}
            />
          )}

          {submitError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2.5">
              {submitError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          {step > 1 ? (
            <Button variant="ghost" onClick={back} disabled={submitting}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
          ) : (
            <span />
          )}

          {step < 4 ? (
            <Button
              onClick={next}
              disabled={
                submitting
                || (step === 1 && !canNext1)
                || (step === 2 && !canNext2)
              }
            >
              Suivant <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={!canNext1 || submitting}>
              {submitting ? 'Création…' : 'Créer le dossier'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Étape 1 ────────────────────────────────────────────────────────────────

function Step1({
  families, familySlug, setFamilySlug, friseSlug, setFriseSlug,
  type, setType, title, setTitle, folderTemplate, setFolderTemplate,
}: {
  families: ParcoursFamily[];
  familySlug: string;
  setFamilySlug: (slug: string) => void;
  friseSlug: string;
  setFriseSlug: (slug: string) => void;
  type: DossierType; setType: (t: DossierType) => void;
  title: string; setTitle: (s: string) => void;
  folderTemplate: string;
  setFolderTemplate: (c: string) => void;
}) {
  const visible = families.filter((family) => frisesOffered(family).length > 0);
  const family = visible.find((item) => item.slug === familySlug) ?? visible[0];
  const frises = offeredFrises(families);
  const folderChoices = [
    { slug: 'none', label: 'Vide' },
    ...families.flatMap((item) => item.folders.filter((folder) => folder.offered).map((folder) => ({
      slug: folder.slug,
      label: folder.name,
    }))),
  ];
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-cime-text-primary mb-1.5">Parcours</label>
        <div className="flex flex-wrap gap-1.5">
          {visible.map((item) => (
            <button
              key={item.slug}
              type="button"
              onClick={() => setFamilySlug(item.slug)}
              className={cn(
                'px-2 py-2 text-xs rounded-md border transition',
                familySlug === item.slug
                  ? 'bg-cime-primary text-white border-cime-primary'
                  : 'bg-white text-cime-text-primary border-neutral-200 hover:border-cime-primary/40',
              )}
            >
              {item.name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-cime-text-primary mb-1.5">Frise</label>
        <div className="flex flex-wrap gap-1.5">
          {frises.map((item) => (
            <button
              key={item.frise.slug}
              type="button"
              onClick={() => setFriseSlug(item.frise.slug)}
              className={cn(
                'px-2 py-2 text-xs rounded-md border transition',
                friseSlug === item.frise.slug
                  ? 'bg-cime-primary text-white border-cime-primary'
                  : 'bg-white text-cime-text-primary border-neutral-200 hover:border-cime-primary/40',
              )}
            >
              {friseChoiceLabel(item, family?.slug)}
            </button>
          ))}
        </div>
        <p className="mt-1.5 text-[11px] text-cime-text-muted">
          {(frises.find((item) => item.frise.slug === friseSlug)?.frise.steps ?? []).join(' → ')}
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium text-cime-text-primary mb-1.5">
          Type d'affaire
        </label>
        <div className="grid grid-cols-4 gap-1.5">
          {TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              className={cn(
                'px-2 py-2 text-xs rounded-md border transition',
                type === t
                  ? 'bg-cime-primary text-white border-cime-primary'
                  : 'bg-white text-cime-text-primary border-neutral-200 hover:border-cime-primary/40',
              )}
            >
              {DOSSIER_TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-cime-text-primary mb-1.5">
          Intitulé du dossier <span className="text-red-500">*</span>
        </label>
        <Input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex : Dupont c/ Martin — Litige bail commercial"
        />
        <p className="mt-1 text-[11px] text-cime-text-muted">
          Le numéro CIME-2026-XXXX sera attribué automatiquement.
        </p>
      </div>

      <FolderTemplatePicker
        value={folderTemplate}
        onChange={setFolderTemplate}
        choices={folderChoices}
        preview={families.flatMap((item) => item.folders).find((folder) => folder.slug === folderTemplate)?.names.join(', ')}
      />
    </>
  );
}

// ─── Étape 2 — Client ───────────────────────────────────────────────────────

function Step2({
  mode, setMode,
  search, setSearch,
  results,
  selected, setSelected,
  newClient, setNewClient,
}: {
  mode: 'existing' | 'new' | 'skip';
  setMode: (m: 'existing' | 'new' | 'skip') => void;
  search: string; setSearch: (s: string) => void;
  results: Contact[];
  selected: Contact | null; setSelected: (c: Contact | null) => void;
  newClient: NewClient; setNewClient: (c: NewClient) => void;
}) {
  return (
    <>
      <div className="grid grid-cols-3 gap-1.5">
        {(['existing', 'new', 'skip'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={cn(
              'px-3 py-2 text-xs rounded-md border transition',
              mode === m
                ? 'bg-cime-primary text-white border-cime-primary'
                : 'bg-white border-neutral-200 hover:border-cime-primary/40',
            )}
          >
            {m === 'existing' && 'Depuis le répertoire'}
            {m === 'new' && 'Nouveau contact'}
            {m === 'skip' && 'Plus tard'}
          </button>
        ))}
      </div>

      {mode === 'existing' && (
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un client par nom, raison sociale, email…"
              className="pl-9"
            />
          </div>
          <div className="border border-neutral-200 rounded-cime divide-y divide-neutral-100 max-h-56 overflow-y-auto">
            {results.length === 0 ? (
              <p className="p-3 text-sm italic text-cime-text-muted text-center">
                {search.trim()
                  ? 'Aucun client trouvé. Essayez « Nouveau contact ».'
                  : 'Commencez à taper pour rechercher un client.'}
              </p>
            ) : (
              results.map((c) => {
                const active = selected?.id === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelected(c)}
                    className={cn(
                      'w-full px-3 py-2 text-left flex items-center justify-between gap-2 transition',
                      active ? 'bg-cime-primary/5' : 'hover:bg-neutral-50',
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-cime-text-primary truncate">
                        {getContactDisplayName(c)}
                      </p>
                      {c.email && (
                        <p className="text-[11px] text-cime-text-muted truncate">{c.email}</p>
                      )}
                    </div>
                    <span className="text-[10px] text-cime-text-muted uppercase tracking-wider shrink-0">
                      {CONTACT_CATEGORY_LABELS[c.contact_category]}
                    </span>
                  </button>
                );
              })
            )}
          </div>
          {selected && (
            <p className="text-[11px] text-cime-text-muted">
              Sélectionné : <span className="font-medium text-cime-text-primary">{getContactDisplayName(selected)}</span>
            </p>
          )}
        </div>
      )}

      {mode === 'new' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {(['individual', 'corporate'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setNewClient({ ...newClient, kind: k })}
                className={cn(
                  'px-3 py-2 text-sm rounded-md border transition',
                  newClient.kind === k
                    ? 'bg-cime-primary text-white border-cime-primary'
                    : 'bg-white border-neutral-200 hover:border-cime-primary/40',
                )}
              >
                {k === 'individual' ? 'Personne physique' : 'Personne morale'}
              </button>
            ))}
          </div>

          {newClient.kind === 'individual' ? (
            <div className="grid grid-cols-2 gap-2">
              <Input
                value={newClient.first_name}
                onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })}
                placeholder="Prénom"
              />
              <Input
                value={newClient.last_name}
                onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })}
                placeholder="Nom *"
              />
            </div>
          ) : (
            <Input
              value={newClient.company_name}
              onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })}
              placeholder="Raison sociale *"
            />
          )}

          <div className="grid grid-cols-2 gap-2">
            <Input
              type="email"
              value={newClient.email}
              onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
              placeholder="Email"
            />
            <Input
              value={newClient.phone}
              onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
              placeholder="Téléphone"
            />
          </div>
          <p className="text-[11px] text-cime-text-muted">
            Le contact sera ajouté au répertoire avec la catégorie « Client ».
            Vous pourrez compléter sa fiche ensuite.
          </p>
        </div>
      )}

      {mode === 'skip' && (
        <p className="text-sm text-cime-text-muted italic py-6 text-center">
          Vous pourrez rattacher un client depuis l'onglet « Résumé » du dossier.
        </p>
      )}
    </>
  );
}

// ─── Étape 3 — Intervenants ─────────────────────────────────────────────────

function Step3({
  members, setMembers,
  available,
  leadOptions,
  leadId,
  onLeadChange,
}: {
  members: MemberDraft[];
  setMembers: (m: MemberDraft[]) => void;
  available: CollaboratorOption[];
  leadOptions: CollaboratorOption[];
  leadId: string;
  onLeadChange: (id: string) => void;
}) {
  const { data: allCollaborators } = useCollaborators();

  function addMember(id: string) {
    setMembers([...members, { collaboratorId: id, role: 'counsel' }]);
  }
  function removeMember(id: string) {
    setMembers(members.filter((m) => m.collaboratorId !== id));
  }
  function changeRole(id: string, role: Exclude<DossierMemberRole, 'lead_counsel'>) {
    setMembers(members.map((m) => (m.collaboratorId === id ? { ...m, role } : m)));
  }

  const lead = leadOptions.find((c) => c.id === leadId);
  const leadName = lead?.full_name || lead?.email || 'Ce collaborateur';

  return (
    <>
      <div>
        <label htmlFor="dossier-lead" className="block text-sm font-medium text-cime-text-primary mb-1.5">
          Responsable du traitement
        </label>
        <CollaboratorSelect
          id="dossier-lead"
          options={leadOptions}
          value={leadId}
          onChange={onLeadChange}
          placeholder="Choisir le responsable"
        />
        <p className="mt-1.5 text-xs text-cime-text-muted">
          <span className="font-medium text-cime-text-primary">{leadName}</span> sera le responsable du dossier.
          Vous pouvez nommer un autre collaborateur que vous.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-cime-text-primary mb-1.5">
          Intervenants supplémentaires <span className="text-cime-text-muted font-normal">— optionnel</span>
        </label>

        {members.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {members.map((m) => {
              const collab = (allCollaborators ?? []).find((c) => c.id === m.collaboratorId);
              const display = collab?.full_name || collab?.email || 'Collaborateur';
              return (
                <div
                  key={m.collaboratorId}
                  className="flex items-center gap-2 px-3 py-2 border border-neutral-200 rounded-md bg-white"
                >
                  <Avatar name={display} src={collab?.avatar_url} size="xs" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-cime-text-primary truncate">{display}</p>
                  </div>
                  <div className="flex gap-1">
                    {(['counsel', 'viewer'] as const).map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => changeRole(m.collaboratorId, r)}
                        className={cn(
                          'px-2 py-1 text-[11px] rounded border transition',
                          m.role === r
                            ? 'bg-cime-primary text-white border-cime-primary'
                            : 'bg-white border-neutral-200 text-cime-text-muted hover:border-cime-primary/40',
                        )}
                      >
                        {ROLE_LABELS[r]}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => removeMember(m.collaboratorId)}
                      className="px-2 py-1 text-neutral-400 hover:text-red-600"
                      aria-label="Retirer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {available.length === 0 ? (
          <p className="text-sm italic text-cime-text-muted text-center py-3">
            {members.length === 0
              ? 'Aucun autre collaborateur actif disponible.'
              : 'Tous les collaborateurs actifs ont été ajoutés.'}
          </p>
        ) : (
          <div className="border border-neutral-200 rounded-cime divide-y divide-neutral-100 max-h-48 overflow-y-auto">
            {available.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => addMember(c.id)}
                className="w-full px-3 py-2 text-left flex items-center justify-between gap-2 hover:bg-neutral-50 transition"
              >
                <CollaboratorOptionRow
                  option={c}
                  trailing={
                    <span className="inline-flex items-center gap-1 text-[11px] text-cime-primary shrink-0">
                      <UserPlus className="w-3.5 h-3.5" /> Ajouter
                    </span>
                  }
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ─── Étape 4 — Détails procéduraux ─────────────────────────────────────────

function Step4({
  jurisdiction, setJurisdiction,
  chambre, setChambre,
  rgNumber, setRgNumber,
  hearingDay, setHearingDay,
  hearingTime, setHearingTime,
}: {
  jurisdiction: Jurisdiction | null; setJurisdiction: (j: Jurisdiction | null) => void;
  chambre: string; setChambre: (s: string) => void;
  rgNumber: string; setRgNumber: (s: string) => void;
  hearingDay: string; setHearingDay: (s: string) => void;
  hearingTime: string; setHearingTime: (s: string) => void;
}) {
  return (
    <>
      <p className="text-xs text-cime-text-muted">
        Tout est optionnel — vous pourrez compléter ces informations depuis l'onglet « Résumé ».
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-cime-text-primary mb-1.5">
            Juridiction
          </label>
          <JurisdictionSelect value={jurisdiction?.id ?? null} onChange={setJurisdiction} />
        </div>
        <div>
          <label className="block text-sm font-medium text-cime-text-primary mb-1.5">
            Chambre
          </label>
          <Input
            value={chambre}
            onChange={(e) => setChambre(e.target.value)}
            placeholder="2e chambre civile…"
            disabled={!jurisdiction}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-cime-text-primary mb-1.5">
            N° RG
          </label>
          <Input
            value={rgNumber}
            onChange={(e) => setRgNumber(e.target.value)}
            placeholder="26/00123"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-cime-text-primary mb-1.5">
          Prochaine audience
        </label>
        <DatePicker
          value={hearingDay}
          onChange={setHearingDay}
          showTime
          time={hearingTime}
          onTimeChange={setHearingTime}
          aria-label="Date et heure de la prochaine audience"
        />
      </div>
    </>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function hasNewClientName(c: NewClient): boolean {
  if (c.kind === 'corporate') return c.company_name.trim().length > 0;
  return c.last_name.trim().length > 0;
}
